// ─────────────────────────────────────────────────────────────────────────
// wedgeRoute - installs the route ("plan your day") engine on
// window.__WEDGE_ROUTE__ so the global-scope prototype (prep-*.jsx, which
// cannot ES-import) can consume it. Mirrors wedgeAnalytics.ts, which installs
// window.__WEDGE_TRACK__.
//
// The engine itself lives in ./route/*.js as pure, unit-tested ES modules
// (the single source of truth). This file is the thin ES->global bridge:
// it re-exports those functions onto a frozen window namespace after first
// paint. No money-path code runs here; the prototype decides when to call it.
//
// Gate: the surface is dark unless window.__WEDGE_FLAGS__.WEDGE_MAPS_V1 === true
// (and NEXT_ON_BLOCK for the pull affordance). This module installs the
// engine regardless; gating is the prototype's job, so the engine is
// available for tests/telemetry even while the UI is dark.
// ─────────────────────────────────────────────────────────────────────────

import { sequenceDay } from './route/sequencer';
import { routeLexicon, routeLabels, readIndustryPrimary } from './route/industryLexicon';
import { buildMapsUrl, buildLegLinks, scrubCoords, MAX_WAYPOINTS } from './route/mapsExport';
import { buildDayBriefingPrompt } from './route/dayBriefingPrompt';

export interface WedgeRouteApi {
  sequenceDay: typeof sequenceDay;
  routeLexicon: typeof routeLexicon;
  routeLabels: typeof routeLabels;
  readIndustryPrimary: typeof readIndustryPrimary;
  buildMapsUrl: typeof buildMapsUrl;
  /** Leg-by-leg two-point nav links · the only cross-platform handoff, since
   *  Apple Maps has no multi-waypoint URL scheme. No waypoint cap. */
  buildLegLinks: typeof buildLegLinks;
  scrubCoords: typeof scrubCoords;
  buildDayBriefingPrompt: typeof buildDayBriefingPrompt;
  MAX_WAYPOINTS: number;
}

const api: WedgeRouteApi = Object.freeze({
  sequenceDay,
  routeLexicon,
  routeLabels,
  readIndustryPrimary,
  buildMapsUrl,
  buildLegLinks,
  scrubCoords,
  buildDayBriefingPrompt,
  MAX_WAYPOINTS,
});

declare global {
  interface Window {
    __WEDGE_ROUTE__?: WedgeRouteApi;
  }
}

/**
 * Install the route engine on window.__WEDGE_ROUTE__. Idempotent: a second
 * call is a no-op so a hot reload or double-invoke never re-freezes. Safe on
 * the server (no-op when window is undefined).
 */
export function initWedgeRoute(): void {
  if (typeof window === 'undefined') return;
  if (window.__WEDGE_ROUTE__) return;
  window.__WEDGE_ROUTE__ = api;
}

export { api as __wedgeRouteApi };
